import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import PersonaAvatar from '../../components/PersonaAvatar'
import personaXpData from '../../data/persona-xp.json'

const AGORA_ACCENT = '#856FC8'

const DIMENSIONS = [
  { key: 'com', label: 'Competence', color: '#E8935A', abbr: 'COM' },
  { key: 'soc', label: 'Social', color: '#6FB8D6', abbr: 'SOC' },
  { key: 'cre', label: 'Creative', color: '#B265D6', abbr: 'CRE' },
  { key: 'wis', label: 'Wisdom', color: '#E6C85A', abbr: 'WIS' },
  { key: 'srv', label: 'Service', color: '#5AC884', abbr: 'SRV' },
  { key: 'idn', label: 'Identity', color: '#D65A93', abbr: 'IDN' },
] as const


const LEVEL_COLORS: Record<number, string> = { 0: '#6B7280', 1: '#6FB8D6', 2: '#E6C85A', 3: '#E8935A' }
const LEVEL_NAMES: Record<number, string> = { 0: 'Newcomer', 1: 'Citizen', 2: 'Contributor', 3: 'Steward' }

const thresholds = personaXpData.thresholds
const THRESHOLD_KEYS = ['citizen', 'contributor', 'steward'] as const

function getProgress(persona: any): { pct: number; blocking: string; blockingKey: string; current: number; required: number } {
  if (persona.level >= 3) return { pct: 100, blocking: '', blockingKey: '', current: 0, required: 0 }
  const next = thresholds[THRESHOLD_KEYS[persona.level]] as any
  if (!next) return { pct: 0, blocking: '', blockingKey: '', current: 0, required: 0 }
  let minPct = Infinity, blocking = '', blockingKey = '', current = 0, required = 0
  for (const dim of DIMENSIONS) {
    const req = next[dim.key] || 0
    const cur = persona[dim.key] || 0
    const pct = req > 0 ? Math.min(100, (cur / req) * 100) : 100
    if (pct < minPct) { minPct = pct; blocking = dim.label; blockingKey = dim.key; current = cur; required = req }
  }
  return { pct: Math.round(minPct), blocking, blockingKey, current, required }
}

interface FeedEvent {
  ts: string
  persona: string
  activity: string
  dims: { key: string; pts: number }[]
}

interface WalletData {
  address: string
  sol_balance: number
  usdc_balance: number
  other_tokens: { symbol: string; amount: number }[]
  explorer_url: string
}

function RadarChart({ persona, size = 200 }: { persona: any; size?: number }) {
  const cx = size / 2, cy = size / 2, r = size * 0.38
  const maxVal = Math.max(...DIMENSIONS.map(d => persona[d.key] || 0), 1)

  const points = DIMENSIONS.map((dim, i) => {
    const angle = (Math.PI * 2 * i) / DIMENSIONS.length - Math.PI / 2
    const val = Math.min((persona[dim.key] || 0) / maxVal, 1)
    return { x: cx + r * val * Math.cos(angle), y: cy + r * val * Math.sin(angle), dim }
  })

  const polygon = points.map(p => `${p.x},${p.y}`).join(' ')

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {[0.25, 0.5, 0.75, 1].map(scale => (
        <polygon key={scale}
          points={DIMENSIONS.map((_, i) => {
            const a = (Math.PI * 2 * i) / DIMENSIONS.length - Math.PI / 2
            return `${cx + r * scale * Math.cos(a)},${cy + r * scale * Math.sin(a)}`
          }).join(' ')}
          fill="none" stroke="var(--border)" strokeWidth={scale === 1 ? 1.5 : 0.5} opacity={0.4}
        />
      ))}
      {DIMENSIONS.map((_, i) => {
        const a = (Math.PI * 2 * i) / DIMENSIONS.length - Math.PI / 2
        return <line key={i} x1={cx} y1={cy} x2={cx + r * Math.cos(a)} y2={cy + r * Math.sin(a)} stroke="var(--border)" strokeWidth={0.5} opacity={0.3} />
      })}
      <defs>
        <linearGradient id="radar-fill" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={AGORA_ACCENT} stopOpacity="0.25" />
          <stop offset="100%" stopColor="#5AC884" stopOpacity="0.15" />
        </linearGradient>
      </defs>
      <polygon points={polygon} fill="url(#radar-fill)" stroke={AGORA_ACCENT} strokeWidth={2} opacity={0.9} />
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r={4} fill={p.dim.color} stroke="var(--bg-base)" strokeWidth={2} />
          <text
            x={cx + (r + 18) * Math.cos((Math.PI * 2 * i) / DIMENSIONS.length - Math.PI / 2)}
            y={cy + (r + 18) * Math.sin((Math.PI * 2 * i) / DIMENSIONS.length - Math.PI / 2)}
            textAnchor="middle" dominantBaseline="central"
            fill={p.dim.color} fontSize={9} fontWeight={700}
          >
            {p.dim.abbr}
          </text>
        </g>
      ))}
    </svg>
  )
}

export default function PersonaEvolvePage() {
  const { name } = useParams<{ name: string }>()
  const persona = name ? (personaXpData.personas as any)[name] : null

  const { data: walletData, isLoading: walletLoading } = useQuery<WalletData | null>({
    queryKey: ['wallet', name],
    queryFn: async () => {
      const urls = [
        `/api/wallets/${name}`,
        `/api/wallets`,
        `http://${window.location.hostname}:5050/api/wallets`,
      ]
      for (const url of urls) {
        try {
          const res = await fetch(url, { signal: AbortSignal.timeout(3000) })
          if (!res.ok) continue
          const data = await res.json()
          if (Array.isArray(data)) return data.find((w: any) => w.persona === name) || null
          if (data?.wallets) return data.wallets.find((w: any) => w.persona === name) || null
          if (data?.address) return data
        } catch { continue }
      }
      return null
    },
    staleTime: 60_000,
    retry: false,
  })

  const { data: feedEvents } = useQuery<FeedEvent[]>({
    queryKey: ['persona-feed', name],
    queryFn: async () => {
      try {
        const res = await fetch(`/api/benchmark/feed?limit=5000&persona=${name}`)
        if (!res.ok) {
          const all = await fetch('/api/benchmark/feed?limit=5000')
          if (!all.ok) return []
          const raw = await all.json()
          const events = Array.isArray(raw) ? raw : raw.events || raw.feed || []
          return events
            .filter((e: any) => e.persona === name)
            .map((e: any) => ({
              ts: e.timestamp || e.ts,
              persona: e.persona,
              activity: e.activity || e.description || e.event,
              dims: e.dimensions
                ? Object.entries(e.dimensions).map(([k, v]) => ({ key: k, pts: v as number }))
                : (e.dims || []),
            }))
            .sort((a: FeedEvent, b: FeedEvent) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
        }
        const raw = await res.json()
        const events = Array.isArray(raw) ? raw : raw.events || raw.feed || []
        return events.map((e: any) => ({
          ts: e.timestamp || e.ts,
          persona: e.persona,
          activity: e.activity || e.description || e.event,
          dims: e.dimensions
            ? Object.entries(e.dimensions).map(([k, v]) => ({ key: k, pts: v as number }))
            : (e.dims || []),
        })).sort((a: FeedEvent, b: FeedEvent) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
      } catch { return [] }
    },
    staleTime: 30_000,
  })

  const { data: badgeData } = useQuery<any>({
    queryKey: ['badges', name],
    queryFn: async () => {
      try {
        const res = await fetch(`/api/badges/persona/${name}`)
        if (!res.ok) return { badges: [] }
        return res.json()
      } catch { return { badges: [] } }
    },
    staleTime: 60_000,
  })
  const badges = badgeData?.badges || []

  const { data: badgeProgressData } = useQuery<any[]>({
    queryKey: ['badge-progress', name],
    queryFn: async () => {
      try {
        const res = await fetch(`/api/badges/progress/${name}`)
        if (!res.ok) return []
        const data = await res.json()
        return Array.isArray(data) ? data : data?.progress || []
      } catch { return [] }
    },
    staleTime: 60_000,
  })
  const nextBadges = (badgeProgressData || []).slice(0, 3)

  const { data: responsivenessData } = useQuery<any>({
    queryKey: ['responsiveness', name],
    queryFn: async () => {
      try {
        const res = await fetch('/api/wizard/responsiveness')
        if (!res.ok) return null
        const data = await res.json()
        return (data.personas || []).find((p: any) => p.persona === name) || null
      } catch { return null }
    },
    staleTime: 60_000,
  })

  if (!persona || !name) {
    return (
      <div style={{ padding: 40, color: 'var(--text-muted)' }}>
        <Link to="/evolve" style={{ color: 'var(--text-muted)', fontSize: 12, textDecoration: 'none' }}>← Back to Evolve</Link>
        <div style={{ marginTop: 20, fontSize: 14 }}>Persona not found</div>
      </div>
    )
  }

  const levelColor = LEVEL_COLORS[persona.level] || '#6B7280'
  const nextLevelColor = LEVEL_COLORS[Math.min(persona.level + 1, 3)]
  const progress = getProgress(persona)
  const events = feedEvents || []
  const totalEvents = events.length
  const totalXpFromFeed = events.reduce((s, e) => s + e.dims.reduce((ss, d) => ss + d.pts, 0), 0)

  return (
    <div style={{ minHeight: '100%', background: 'var(--bg-base)', color: 'var(--text-primary)' }}>
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 24px' }}>
        <Link to="/evolve" style={{ color: 'var(--text-muted)', fontSize: 12, textDecoration: 'none', display: 'block', marginBottom: 12 }}>
          ← Back to Evolve
        </Link>

        {/* ═══ Header Card ═══ */}
        <div style={{
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
          borderRadius: 14, borderTop: `3px solid ${levelColor}`,
          padding: '24px 28px', marginBottom: 20,
          display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap',
        }}>
          {/* Left: Avatar + Info */}
          <div style={{ display: 'flex', gap: 16, flex: '1 1 300px', minWidth: 0 }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <PersonaAvatar name={name} size={56} />
              <div style={{
                position: 'absolute', bottom: -4, right: -4, width: 20, height: 20,
                borderRadius: '50%', background: levelColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 800, color: '#fff',
                border: '2px solid var(--bg-surface)',
              }}>
                {persona.level}
              </div>
            </div>
            <div>
              <h1 style={{
                fontFamily: 'Playfair Display, Georgia, serif',
                fontSize: 26, fontWeight: 700, margin: '0 0 4px 0', textTransform: 'capitalize',
              }}>
                {name}
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                <span style={{
                  padding: '3px 10px', borderRadius: 8, fontWeight: 700, fontSize: 10,
                  background: `${levelColor}18`, color: levelColor,
                  border: `1px solid ${levelColor}30`, textTransform: 'uppercase', letterSpacing: 0.5,
                }}>
                  {LEVEL_NAMES[persona.level]}
                </span>
                {persona.circle && (
                  <span style={{ textTransform: 'capitalize', opacity: 0.7 }}>{persona.circle} Circle</span>
                )}
                {persona.ceremony && persona.ceremony !== 'None' && (
                  <span style={{ opacity: 0.6 }}>{persona.ceremony}</span>
                )}
              </div>
              {/* Level Progress */}
              {persona.level < 3 && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                      → <span style={{ color: nextLevelColor, fontWeight: 600 }}>{LEVEL_NAMES[persona.level + 1]}</span>
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: progress.pct >= 80 ? '#5AC884' : nextLevelColor }}>
                      {progress.pct}%
                    </span>
                  </div>
                  <div style={{ height: 6, borderRadius: 3, background: 'var(--bg-elevated)', overflow: 'hidden', width: 200 }}>
                    <div style={{
                      width: `${progress.pct}%`, height: '100%', borderRadius: 3,
                      background: `linear-gradient(90deg, ${levelColor}88, ${nextLevelColor})`,
                      transition: 'width 0.5s',
                    }} />
                  </div>
                  {progress.blocking && (
                    <div style={{ fontSize: 9, color: '#ef4444', marginTop: 3, fontWeight: 600 }}>
                      Blocked: {progress.blocking} ({progress.current}/{progress.required})
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right: XP Summary */}
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
            gap: 4, flexShrink: 0,
          }}>
            <div style={{
              fontSize: 32, fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1,
              background: 'linear-gradient(135deg, #E6C85A, #E8935A, #D65A93)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              {persona.total.toLocaleString()}
            </div>
            <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 600 }}>
              Total XP
            </div>
          </div>
        </div>

        {/* ═══ Two Column: Wallet + Radar ═══ */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
          {/* Wallet Card */}
          <div style={{
            flex: '1 1 280px', minWidth: 0,
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 12, padding: '18px 20px',
          }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>
              Wallet
            </div>
            {walletData ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <div style={{
                    fontFamily: "'SF Mono', Menlo, monospace", fontSize: 11,
                    color: 'var(--text-secondary)', flex: 1, minWidth: 0,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {walletData.address}
                  </div>
                  {walletData.explorer_url && (
                    <a href={walletData.explorer_url} target="_blank" rel="noreferrer"
                      style={{ fontSize: 10, color: AGORA_ACCENT, textDecoration: 'none', flexShrink: 0 }}>
                      ↗ Solscan
                    </a>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{
                    padding: '10px 16px', borderRadius: 10,
                    background: 'var(--bg-elevated)', flex: '1 1 80px',
                  }}>
                    <div style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {walletData.sol_balance.toFixed(4)}
                    </div>
                    <div style={{ fontSize: 9, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>SOL</div>
                  </div>
                  <div style={{
                    padding: '10px 16px', borderRadius: 10,
                    background: 'var(--bg-elevated)', flex: '1 1 80px',
                  }}>
                    <div style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {walletData.usdc_balance.toFixed(2)}
                    </div>
                    <div style={{ fontSize: 9, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>USDC</div>
                  </div>
                  {walletData.other_tokens?.filter(t => t.amount > 0).map(t => (
                    <div key={t.symbol} style={{
                      padding: '10px 16px', borderRadius: 10,
                      background: 'var(--bg-elevated)', flex: '1 1 80px',
                    }}>
                      <div style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        {t.amount < 1 ? t.amount.toFixed(4) : t.amount.toFixed(2)}
                      </div>
                      <div style={{ fontSize: 9, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{t.symbol}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', opacity: 0.6 }}>
                {walletLoading ? 'Loading wallet...' : 'No wallet provisioned yet'}
              </div>
            )}
          </div>

          {/* Radar Chart Card */}
          <div style={{
            flex: '0 0 auto',
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 12, padding: '18px 20px',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
          }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8, alignSelf: 'flex-start' }}>
              Dimensions
            </div>
            <RadarChart persona={persona} size={180} />
          </div>
        </div>

        {/* ═══ Badges ═══ */}
        {badges.length > 0 && (
          <div style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 12, padding: '18px 20px', marginBottom: 20,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
              Badges ({badges.length})
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {badges.map((b: any) => (
                <div key={b.id} title={b.description} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 14px', borderRadius: 10,
                  background: `${b.color || AGORA_ACCENT}10`,
                  border: `1px solid ${b.color || AGORA_ACCENT}25`,
                }}>
                  <span style={{ fontSize: 18 }}>{b.icon}</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: b.color || 'var(--text-primary)' }}>{b.name}</div>
                    <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.3 }}>
                      {b.type}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ Next Badges ═══ */}
        {nextBadges.length > 0 && (
          <div style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 12, padding: '18px 20px', marginBottom: 20,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
              Next Badges
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {nextBadges.map((b: any) => (
                <div key={b.badge_id || b.id} style={{
                  padding: '12px 16px', borderRadius: 10,
                  background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span style={{ fontSize: 20 }}>{b.icon}</span>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{b.name}</span>
                      <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 8 }}>{b.type}</span>
                    </div>
                    <span style={{
                      fontSize: 12, fontWeight: 700,
                      color: b.overallProgress >= 80 ? '#5AC884' : b.overallProgress >= 50 ? '#E6C85A' : 'var(--text-muted)',
                    }}>
                      {b.overallProgress}%
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {(b.requirements || []).map((r: any, i: number) => {
                      const pct = Math.min(100, r.pct || 0)
                      const color = pct >= 100 ? '#5AC884' : pct >= 60 ? '#E6C85A' : '#E8935A'
                      return (
                        <div key={i} style={{ flex: '1 1 120px', minWidth: 100 }}>
                          <div style={{ fontSize: 9, color: 'var(--text-muted)', marginBottom: 3 }}>
                            {r.type?.replace(/_/g, ' ')} {r.current}/{r.target}
                          </div>
                          <div style={{ height: 4, borderRadius: 2, background: 'var(--bg-base)', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 2, transition: 'width 0.3s' }} />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ Dimension Bars ═══ */}
        <div style={{
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
          borderRadius: 12, padding: '18px 20px', marginBottom: 20,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 12 }}>
            {DIMENSIONS.map(dim => {
              const val = persona[dim.key] || 0
              const maxForBar = Math.max(...DIMENSIONS.map(d => persona[d.key] || 0), 1)
              const pct = Math.min(100, (val / maxForBar) * 100)
              const isBottleneck = persona.bottleneck === dim.key
              return (
                <div key={dim.key} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '6px 0',
                }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, color: dim.color,
                    width: 30, textAlign: 'right', letterSpacing: 0.5,
                  }}>
                    {dim.abbr}
                  </span>
                  <div style={{
                    flex: 1, height: 8, borderRadius: 4,
                    background: 'var(--bg-elevated)', overflow: 'hidden',
                    position: 'relative',
                  }}>
                    <div style={{
                      width: `${pct}%`, height: '100%', borderRadius: 4,
                      background: `linear-gradient(90deg, ${dim.color}88, ${dim.color})`,
                      transition: 'width 0.5s',
                    }} />
                  </div>
                  <span style={{
                    fontSize: 11, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                    width: 45, textAlign: 'right',
                    color: isBottleneck ? '#ef4444' : 'var(--text-secondary)',
                  }}>
                    {val.toLocaleString()}
                  </span>
                  {isBottleneck && (
                    <span style={{ fontSize: 8, color: '#ef4444', fontWeight: 700 }}>▼</span>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* ═══ Responsiveness ═══ */}
        <ResponsivenessSection persona={name!} />

        {/* ═══ Responsiveness ═══ */}
        {responsivenessData && (
          <div style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 12, padding: '18px 20px', marginBottom: 20,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                Responsiveness
              </div>
              <div style={{
                fontSize: 20, fontWeight: 800,
                color: responsivenessData.grade === 'A' ? '#5AC884'
                  : responsivenessData.grade === 'B' ? '#6FB8D6'
                  : responsivenessData.grade === 'C' ? '#E6C85A'
                  : responsivenessData.grade === 'D' ? '#E8935A'
                  : '#f85149',
              }}>
                {responsivenessData.grade}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
              {[
                { label: 'Response Rate', value: `${responsivenessData.response_rate || 0}%`, color: (responsivenessData.response_rate || 0) >= 80 ? '#5AC884' : (responsivenessData.response_rate || 0) >= 50 ? '#E6C85A' : '#f85149' },
                { label: 'Messages', value: responsivenessData.total_messages || 0, color: 'var(--text-secondary)' },
                { label: 'Acknowledged', value: responsivenessData.acknowledged || 0, color: '#5AC884' },
                { label: 'Pending', value: responsivenessData.pending || 0, color: (responsivenessData.pending || 0) > 0 ? '#E6C85A' : 'var(--text-muted)' },
                { label: 'Avg Retries', value: (responsivenessData.avg_retries || 0).toFixed(1), color: (responsivenessData.avg_retries || 0) > 2 ? '#f85149' : 'var(--text-secondary)' },
                { label: 'Escalated', value: responsivenessData.escalated || 0, color: (responsivenessData.escalated || 0) > 0 ? '#f85149' : 'var(--text-muted)' },
              ].map(s => (
                <div key={s.label} style={{ padding: '8px 0' }}>
                  <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                    {s.label}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: s.color, fontVariantNumeric: 'tabular-nums' }}>
                    {s.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ XP Growth Feed ═══ */}
        <div style={{
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
          borderRadius: 12, overflow: 'hidden',
        }}>
          <div style={{
            padding: '14px 20px', borderBottom: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
              XP Growth Feed
            </div>
            <div style={{ display: 'flex', gap: 12, fontSize: 10, color: 'var(--text-muted)' }}>
              <span>{totalEvents} events</span>
              <span style={{ color: '#5AC884', fontWeight: 600 }}>+{totalXpFromFeed.toLocaleString()} XP</span>
            </div>
          </div>

          {events.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              No growth events recorded yet.
            </div>
          ) : (
            <div style={{ maxHeight: 600, overflowY: 'auto' }}>
              {events.map((ev, i) => {
                const d = new Date(ev.ts.endsWith('Z') || ev.ts.includes('T') ? ev.ts : ev.ts + 'Z')
                const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' })
                const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York' })
                const totalPts = ev.dims.reduce((s, d) => s + d.pts, 0)
                return (
                  <div
                    key={i}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 20px',
                      borderBottom: i < events.length - 1 ? '1px solid var(--border)' : 'none',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-elevated)' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                  >
                    <div style={{ width: 70, flexShrink: 0, fontSize: 10, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                      <div>{dateStr}</div>
                      <div style={{ fontSize: 9, opacity: 0.7 }}>{timeStr}</div>
                    </div>
                    <div style={{ flex: 1, fontSize: 12, color: 'var(--text-secondary)', minWidth: 0 }}>
                      {ev.activity}
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {ev.dims.map((d, j) => {
                        const dim = DIMENSIONS.find(dd => dd.key === d.key)
                        return (
                          <span key={j} style={{
                            fontSize: 9, padding: '2px 6px', borderRadius: 6,
                            background: `${dim?.color || '#6B7280'}15`,
                            color: dim?.color || '#6B7280',
                            fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                          }}>
                            +{d.pts} {dim?.abbr || d.key.toUpperCase()}
                          </span>
                        )
                      })}
                    </div>
                    <div style={{
                      width: 45, textAlign: 'right', flexShrink: 0,
                      fontSize: 11, fontWeight: 700, color: '#5AC884',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      +{totalPts}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function ResponsivenessSection({ persona }: { persona: string }) {
  const { data } = useQuery<any>({
    queryKey: ['responsiveness', persona],
    queryFn: async () => {
      try {
        const res = await fetch(`/api/wizard/responsiveness?persona=${persona}`)
        if (!res.ok) return null
        const all = await res.json()
        const list = Array.isArray(all) ? all : all?.personas || []
        return list.find((p: any) => p.persona === persona) || null
      } catch { return null }
    },
    staleTime: 60_000,
  })

  if (!data) return null

  const gradeColors: Record<string, string> = {
    A: '#5AC884', B: '#6FB8D6', C: '#E6C85A', D: '#E8935A', F: '#f85149',
  }
  const grade = data.grade || '—'
  const color = gradeColors[grade] || 'var(--text-muted)'
  const rate = data.responseRate != null ? Math.round(data.responseRate * 100) : null

  return (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 12, padding: '18px 20px', marginBottom: 20,
    }}>
      <div style={{
        fontSize: 10, fontWeight: 600, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12,
      }}>
        Responsiveness
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12,
          background: `${color}15`, border: `2px solid ${color}40`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 24, fontWeight: 800, color,
        }}>
          {grade}
        </div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {rate != null && (
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{rate}%</div>
              <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Response Rate</div>
            </div>
          )}
          {data.totalMessages != null && (
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{data.totalMessages}</div>
              <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Messages</div>
            </div>
          )}
          {data.avgRetries != null && (
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{data.avgRetries.toFixed(1)}</div>
              <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Avg Retries</div>
            </div>
          )}
          {data.escalatedCount != null && data.escalatedCount > 0 && (
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: '#f85149' }}>{data.escalatedCount}</div>
              <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Escalated</div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
